<template>
  <div
    class="treatments-card"
    :aria-label="treatment.title"
  >
    <div class="treatments-card-wrapper">
      <div class="treatments-card-content">
        <div class="treatments-card-info">
          <h3 class="treatments-card-title">
            {{ treatment.title }}
          </h3>
          <div class="treatments-card-desc">
            <p
              :id="`desc-${treatment.spaSoftId}`"
              class="desc-wrapper"
              v-html="truncatedDescription"
            />
            <button
              v-if="isDescriptionTruncated"
              type="button"
              class="toggle-button"
              :class="{'more': !showFullDescription, 'less': showFullDescription}"
              :aria-expanded="showFullDescription"
              :aria-controls="`desc-${treatment.spaSoftId}`"
              @click.prevent="toggleDescriptionVisibility"
              @keydown.enter="toggleDescriptionVisibility"
              @keydown.space.prevent="toggleDescriptionVisibility"
            >
              {{ showFullDescription
                ? tc('spaSoft.step1.treatmentCards.seeLess')
                : tc('spaSoft.step1.treatmentCards.seeMore')
              }}
            </button>
          </div>
        </div>
      </div>
      <div
        class="schedule-list"
        role="radiogroup"
      >
        <label
          v-for="(variant, key) in treatment.propertyFacilitySpaTreatmentsVariantModel"
          :key="`time-${key}`"
          :for="`${variant.spaSoftId}-time-guest-${currentGuestNumber}`"
          class="schedule-item"
        >
          <input
            v-show="treatment.propertyFacilitySpaTreatmentsVariantModel.length > 1"
            :id="`${variant.spaSoftId}-time-guest-${currentGuestNumber}`"
            v-model="selectedTimeSlot"
            type="radio"
            :name="`timeslot-${treatment.id}`"
            :value="variant"
          >

          <div
            v-if="variant.duration"
            class="duration"
            :aria-description="tc('spaSoft.step1.treatmentCards.ariaLabelDuration')"
          >
            {{ variant.duration }}
          </div>
          <div
            v-if="variant.dayAndTime"
            class="time"
            :aria-description="tc('spaSoft.step1.treatmentCards.ariaLabelDayAndTime')"
          >
            {{ variant.dayAndTime }}
          </div>
          <div
            v-if="variant.apiTreatmentDetails.price.totalCalculated"
            class="price"
            :aria-description="tc('spaSoft.step1.treatmentCards.ariaLabelPrice')"
          >
            {{ getTreatmentPriceFormatted({ treatment: variant, checkTaxes: true }) }}
          </div>
        </label>
      </div>
    </div>
    <div
      class="treatments-card-ctas"
    >
      <contact-modal v-if="treatment.notBookableOnline">
        <div
          slot-scope="{ activate }"
          class=""
        >
          <button
            class="button--secondary black size-sm outlined"
            :aria-label="tc('spaSoft.step1.treatmentCards.ariaLabelContactCta', { treatmentTitle: treatment.title })"
            @click="activate"
            @keydown.space.prevent="activate"
            @keydown.enter.prevent="activate"
          >
            {{ tc('spaSoft.step1.treatmentCards.contactCta') }}
          </button>
        </div>
      </contact-modal>
      <button
        v-else
        class="button--primary black size-sm primary"
        :disabled="addCtaIsDisabled"
        :aria-label="tc('spaSoft.step1.treatmentCards.ariaLabelAddCta', { treatmentTitle: treatment.title, guestNumber: Number(currentGuestNumber) + 1 })"
        @click="handleAddClick"
        @keypress.enter.prevent="handleAddClick"
        @keypress.space.prevent="handleAddClick"
      >
        {{ tc('spaSoft.step1.treatmentCards.addCta') }}
      </button>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapState, mapActions } from 'vuex'
  import { v4 as uuidv4 } from 'uuid'
  import Tracking from '@mixins/tracking'
  import ContactModal from './contact-modal.vue'

  export default {
    name: 'TreatmentsCard',
    components: {
      ContactModal,
    },
    mixins: [Tracking],
    inject: ['currentGuestNumber'],
    props: {
      treatment: {
        type: Object,
        required: true,
        default: () => ({})
      },
      index: {
        type: Number,
        required: true,
      },
    },
    data() {
      return {
        showFullDescription: false,
        selectedTimeSlot: null,
      }
    },
    computed: {
      ...mapState('responsive', { wWidth: 'width', wHeight: 'height' }),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('spaSoft', ['guestsSelectedData', 'getTreatmentPriceFormatted', 'getTotalCost']),
      ...mapState('spaSoft', ['hotelCode', 'propertyData', 'numberOfGuests']),
      ...mapGetters('currency', ['getDisplayPrice', 'orSelected']),
      currentGuestData() {
        return this.guestsSelectedData[this.currentGuestNumber]
      },
      truncatedDescription() {
        const maxLength = this.descMaxLength
        if (!this.showFullDescription
          && this.treatment.description
          && this.treatment.description.length > maxLength) {
          return `${this.treatment.description.slice(0, maxLength)}...`
        }
        return this.treatment.description
      },

      isDescriptionTruncated() {
        return this.treatment.description && this.treatment.description.length > this.descMaxLength
      },

      descMaxLength() {
        return 130
      },

      addCtaIsDisabled() {
        return this.selectedTimeSlot === null
      },

      cartHadItemsBeforeSelection() {
        const cartLength = Object.values(this.guestsSelectedData).reduce((acc, guest) => (
          acc + guest.selectedTreatments.length
        ), 0)
        return cartLength > 1
      }
    },
    mounted() {
      if (this.treatment.propertyFacilitySpaTreatmentsVariantModel.length === 1) {
        this.selectedTimeSlot = this.treatment.propertyFacilitySpaTreatmentsVariantModel[0]
      }
    },
    methods: {
      ...mapActions('spaSoft', ['addTreatment', 'trackSpaViewCart']),
      toggleDescriptionVisibility() {
        this.showFullDescription = !this.showFullDescription
      },
      handleAddClick() {
        const order = this.currentGuestData.selectedTreatments
          ? this.currentGuestData.selectedTreatments.length + 1
          : 1
        this.addTreatment({
          guest: this.currentGuestNumber,
          treatment: {
            uuid: uuidv4(),
            isActive: true,
            order,
            sitecoreTreatmentDetails: this.treatment,
            selectedTimeSlot: {
              ...this.selectedTimeSlot,
              preferredGender: '',
            },
            treatmentCategory: {
              spaSoftId: this.currentGuestData.selectedCategory.spaSoftId,
              title: this.currentGuestData.selectedCategory.title
            }
          }
        })
        this.handleTreatmentTracking()
        this.$emit('treatment-selected')

        this.$nextTick(() => {
          document.querySelector('.selected-treatments__list .selected-treatments__card:last-child').focus()
        })
      },
      handleTreatmentTracking() {
        const productToTrack = {
          guestNumber: Number(this.currentGuestNumber) + 1,
          productType: 'Treatment',
          treatmentType: this.currentGuestData.selectedCategory.title,
          treatmentId: this.selectedTimeSlot?.spaSoftId || null,
          treatmentName: this.treatment?.title || null,
          duration: this.selectedTimeSlot?.duration || null,
          price: this.getDisplayPrice({
            price: this.selectedTimeSlot?.apiTreatmentDetails?.price?.totalCalculated,
            fromCurrency: this.propertyData?.currency?.isoCode,
            toCurrency: this.orSelected(this.propertyData?.currency?.isoCode),
            noSymbol: true
          }),
          currency: this.orSelected(this.propertyData?.currency?.isoCode),
          listPosition: this.index + 1,
        }

        this.$track(this.getTrackingData('spaSoft', null, {
          event: 'spaAddToCart',
          hotelCode: this.hotelCode,
          stepNumber: 1.2,
          products: [productToTrack],
        }))

        if (this.cartHadItemsBeforeSelection) {
          this.$track(this.getTrackingData('spaSoft', null, {
            event: 'spaCartUpdate_Treatment',
            hotelCode: this.hotelCode,
            stepNumber: 1.4,
            cart: {
              numberOfGuests: this.numberOfGuests,
              cartTotalPrice: this.getTotalCost(),
              currency: this.orSelected(this.propertyData?.currency?.isoCode),
              products: [productToTrack],
            }
          }))
          this.$nextTick(() => {
            this.trackSpaViewCart()
          })
        }
      }
    },
  }
</script>
